import React, { useState, useEffect } from "react";
import PageHero from "../components/PageHero";
import { getProducts, getSettings } from "../api";
import {
  ArrowUpRight,
  ChevronRight,
  ShieldCheck,
  Layers3,
  Factory,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";

export default function Products() {
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState({});
  const [searchParams] = useSearchParams();

  const [active, setActive] = useState(
    Number(searchParams.get("product")) || 0
  );

  useEffect(() => {
    getProducts().then((res) => {
      setProducts(res || []);
    });

    getSettings().then(setSettings);
  }, []);

  const p = products[active] || {};

  const grades = Array.isArray(p.grades)
    ? p.grades
    : String(p.grades || "")
        .split(",")
        .map((g) => g.trim())
        .filter(Boolean);

        const extractSectionItems = (html, heading) => {
  if (!html) return [];

  const temp = document.createElement("div");
  temp.innerHTML = html;

  const items = [];
  let capture = false;

  temp.childNodes.forEach((node) => {
    const text = node.textContent?.trim();

    if (!text) return;

    // detect heading
    if (
      text.toLowerCase().includes(heading.toLowerCase())
    ) {
      capture = true;
      return;
    }

    // stop when next section starts
    if (
      capture &&
      (
        text.toLowerCase().includes("applications") ||
        text.toLowerCase().includes("key benefits")
      ) &&
      !text.toLowerCase().includes(heading.toLowerCase())
    ) {
      capture = false;
    }

    if (
      capture &&
      node.tagName === "P" &&
      !text.toLowerCase().includes(heading.toLowerCase())
    ) {
      items.push(
        text.replace(/^•/, "").trim()
      );
    }
  });

  return items;
};

const benefits = extractSectionItems(
  p.description_2,
  "Key Benefits"
);

const applications = extractSectionItems(
  p.description_2,
  "Applications"
);

  return (
    <main className="bg-white">
      <PageHero
        title="Products"
        subtitle={
          settings.products_hero_subtitle ||
          "Engineered ready-mix concrete solutions designed for residential, commercial, and infrastructure applications."
        }
        num="02"
      />

      <section className="py-20 bg-[#f7f7f8] overflow-hidden">
        <div className="max-w-[1450px] mx-auto px-5 md:px-8">
          {/* TOP PRODUCT SECTION */}

          {/* TOP PRODUCT SECTION */}

{products.length > 0 && (
  <div className="space-y-14 mb-24">

    {/* MAIN SECTION */}

    <div className="grid xl:grid-cols-[48%,52%] gap-8 items-stretch">

      {/* LEFT IMAGE */}

      <div className="relative rounded-[34px] overflow-hidden min-h-[620px] bg-black shadow-[0_25px_80px_rgba(0,0,0,0.12)]">

        <img
          src={
            p.image ||
            "https://images.unsplash.com/photo-1504307651254-35680f356dfd?q=80&w=1600"
          }
          alt={p.name}
          className="w-full h-full object-cover"
        />

        {/* OVERLAY */}

        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-transparent" />

        {/* CONTENT */}

        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-10">

          {/* BADGE */}

          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 backdrop-blur-xl px-4 py-2 mb-5">

            <span className="w-2 h-2 rounded-full bg-[#ef2435]" />

            <span className="text-white text-[10px] uppercase tracking-[0.3em] font-semibold">
              Featured Product
            </span>
          </div>

          {/* TYPE */}

          <p className="text-white/60 text-[11px] uppercase tracking-[0.35em] mb-3">
            {p.type}
          </p>

          {/* TITLE */}

          <h2 className="text-white text-4xl md:text-6xl font-black leading-[0.95] mb-5">
            {p.name}
          </h2>

          {/* DESC */}

          <p className="text-white/75 text-[15px] leading-8 max-w-2xl">
            {p.description}
          </p>

          {/* TAGS */}

          <div className="flex flex-wrap gap-3 mt-7">

            {grades.length > 0 ? (
              grades.map((g) => (
                <span
                  key={g}
                  className="px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/10 text-white text-[11px] font-semibold"
                >
                  {g}
                </span>
              ))
            ) : (
              <span className="px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/10 text-white text-[11px] font-semibold">
                Premium Mix
              </span>
            )}
          </div>
        </div>
      </div>

      {/* RIGHT SIDE */}

      <div className="flex flex-col gap-5 h-full">

        {/* OVERVIEW */}

        <div className="bg-white border border-[#ececef] rounded-[30px] p-8 shadow-sm">

          <p className="text-[#ef2435] text-[11px] uppercase tracking-[0.35em] font-semibold mb-3">
            Product Overview
          </p>

          <h3 className="text-3xl md:text-4xl font-black text-gray-900 leading-[1.05] mb-6">
            Engineered For
            <br />
            Modern Infrastructure
          </h3>

          <p className="text-gray-600 leading-8 text-[15px]">
            {p.description}
          </p>
        </div>

        {/* BENEFITS + APPLICATIONS */}

        <div className="grid md:grid-cols-2 gap-5 flex-1">

          {/* BENEFITS */}

          <div className="rounded-[30px] bg-[#fafafa] border border-[#ececef] p-7">

            {/* HEADER */}

            <div className="flex items-center gap-3 mb-8">

              <div className="w-11 h-11 rounded-2xl bg-[#ef2435] flex items-center justify-center">

                <ShieldCheck
                  className="text-white"
                  size={18}
                />
              </div>

              <div>

                <p className="text-[#ef2435] text-[10px] uppercase tracking-[0.3em] font-semibold mb-1">
                  Benefits
                </p>

                <h4 className="text-2xl font-black text-gray-900 leading-none">
                  Key Advantages
                </h4>
              </div>
            </div>

            {/* LIST */}

            <div className="space-y-5">

              {benefits.map((item, i) => (
                <div
                  key={i}
                  className="flex items-start gap-4"
                >

                  <span className="text-[#ef2435] font-black text-sm mt-1">
                    {String(i + 1).padStart(2, "0")}
                  </span>

                  <p className="text-gray-700 text-[15px] leading-7">
                    {item}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* APPLICATIONS */}

          <div className="rounded-[30px] bg-[#111111] p-7 relative overflow-hidden">

            {/* GLOW */}

            <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#ef2435]/10 blur-3xl" />

            <div className="relative z-10">

              {/* HEADER */}

              <div className="flex items-center gap-3 mb-8">

                <div className="w-11 h-11 rounded-2xl bg-[#ef2435] flex items-center justify-center">

                  <Factory
                    className="text-white"
                    size={18}
                  />
                </div>

                <div>

                  <p className="text-[#ef2435] text-[10px] uppercase tracking-[0.3em] font-semibold mb-1">
                    Applications
                  </p>

                  <h4 className="text-2xl font-black text-white leading-none">
                    Usage Areas
                  </h4>
                </div>
              </div>

              {/* LIST */}

              <div className="space-y-5">

                {applications.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-start justify-between gap-4 border-b border-white/10 pb-4"
                  >

                    <div className="flex gap-4">

                      <span className="text-white/40 font-black text-sm mt-1">
                        {String(i + 1).padStart(2, "0")}
                      </span>

                      <p className="text-white/90 text-[15px] leading-7">
                        {item}
                      </p>
                    </div>

                    <ArrowUpRight
                      size={15}
                      className="text-white/30 shrink-0 mt-1"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
)}

          {/* PRODUCT LIST */}

          <div className="mb-10">
            <span className="inline-block text-xs uppercase tracking-[0.3em] text-[#ef2435] font-semibold mb-4">
              [ FULL PRODUCT LINEUP ]
            </span>

            <h2 className="text-5xl md:text-6xl font-black text-gray-900 leading-none">
              SELECT YOUR MIX.
            </h2>
          </div>

          {/* PRODUCT CARDS */}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">

  {products.map((pr, i) => (
    <button
      key={pr.id || i}
      onClick={() => {
        setActive(i);

        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }}
      className={`group overflow-hidden rounded-[28px] text-left transition-all duration-300 ${
        i === active
          ? "bg-[#111111]"
          : "bg-white border border-[#ececef] hover:-translate-y-1"
      }`}
    >

      {/* IMAGE */}

      <div className="relative h-[200px] overflow-hidden">

        <img
          src={pr.image}
          alt={pr.name}
          className="w-full h-full object-cover group-hover:scale-105 transition duration-700"
        />

        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />

        <div className="absolute bottom-5 left-5 right-5">

          <p className="text-white/60 text-[10px] uppercase tracking-[0.3em] mb-2">
            {pr.type}
          </p>

          <h3 className="text-white text-2xl font-black leading-none">
            {pr.name}
          </h3>
        </div>
      </div>

      {/* CONTENT */}

      <div className="p-6">

        <p
          className={`text-sm leading-7 line-clamp-3 mb-6 ${
            i === active
              ? "text-white/70"
              : "text-gray-600"
          }`}
        >
          {pr.description}
        </p>

        <div className="flex items-center justify-between">

          <span
            className={`text-xs uppercase tracking-[0.25em] font-semibold ${
              i === active
                ? "text-[#ef2435]"
                : "text-gray-500"
            }`}
          >
            View Product
          </span>

          <ChevronRight
            size={18}
            className={
              i === active
                ? "text-white"
                : "text-gray-500"
            }
          />
        </div>
      </div>
    </button>
  ))}
</div>
        </div>
      </section>
    </main>
  );
}